/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-misused-promises */
import { createFilterOptions } from "@mui/material/useAutocomplete";
import {
  SmartAutoComplete,
  SmartInputBaseProps,
  usePopup,
} from "@nodeon/react-components";
import React, { useEffect, useState } from "react";
import {
  FieldPathValue,
  FieldValues,
  Path,
  UnpackNestedValue,
  UseFormReturn,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useComparisonLevelApi } from "~/api/GeneratedApi";
import ComparisonLevelDialog from "~/pages/marine-survey-and-design-page/dialogs/ComparisonLevelDialog";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

interface Props<FormT extends FieldValues> {
  readOnly?: boolean;
  name: Path<FormT>;
  formMethods: UseFormReturn<FormT>;
  gridProps?: SmartInputBaseProps<FormT>["gridProps"];
}

const filter = createFilterOptions<number>();

const ComparisonLevelAutocomplete = <FormT extends FieldValues>({
  formMethods,
  readOnly,
  name,
  gridProps,
}: Props<FormT>): JSX.Element | null => {
  const { t } = useTranslation("comparison-level");
  const { show } = usePopup();
  const { getComparisonLevels } = useComparisonLevelApi();
  const [inputValue, setInputValue] = useState("");
  const [hasValue, setHasValue] = useState(false);
  const { setValue, watch } = formMethods;
  const watchVal = watch(name);

  useEffect(() => {
    setHasValue(watchVal !== null && watchVal !== undefined);
  }, [watchVal]);

  const { data, isLoading, refetch } = useCustomQuery(
    "comparison-levels",
    () => getComparisonLevels(),
    {
      onSuccess: (d) => {
        const val = d.results.find((et) => et.name === inputValue);
        if (val) {
          setValue(
            name,
            val.id as UnpackNestedValue<FieldPathValue<FormT, typeof name>>,
          );
        }
      },
    },
  );

  return isLoading ? null : (
    <SmartAutoComplete<FormT, number, undefined, undefined, true>
      name={name}
      label={t("label")}
      gridProps={gridProps}
      fullWidth
      options={data?.results.map((e) => e.id) ?? []}
      freeSolo
      getOptionLabel={(opt) => {
        if (opt >= 0) {
          return (
            data?.results.find((x) => x.id === opt)?.name ??
            t("general:unknown")
          );
        }
        if (opt === Number.NEGATIVE_INFINITY) {
          return !inputValue
            ? `(${t("general:add-new")})`
            : `(${t("general:add")} ${inputValue})`;
        }
        return "";
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        if (!hasValue) {
          filtered.push(Number.NEGATIVE_INFINITY);
        }
        return filtered;
      }}
      checkAdd={(val) => !data?.results.find((x) => x.id === val)}
      onAdd={() =>
        show(
          <ComparisonLevelDialog
            onSubmitted={(val) => {
              setInputValue(val);
              refetch();
            }}
            initialValue={inputValue}
          />,
          { noDialog: true },
        )
      }
      onInputChange={(_, v) => setInputValue(v)}
      disabled={readOnly}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
    />
  );
};

export default ComparisonLevelAutocomplete;
